// F145: may the profile step say "první podklady právě vznikají"?
//
// Pavel, 2026-08-13 (voice, screenshot of the step):
//   "Píšeš 'První podklady právě vznikají.' To dává smysl, POKUD fakt
//    vznikají. Pokud nevznikají, protože se prostě nepodařilo je vytvořit,
//    tak napiš: ve zdrojích jsme o tobě nic nenašli. Prosím, zapni nahrávání
//    a představ se nám."
//
// So the sentence is CONDITIONAL on a fact, and the fact is the research job.
// Before F145 the surface asked two booleans (`profileResearchPending`,
// `profileResearchFailed`) and could answer only three things; the two that
// were wrong are:
//
//   * a queued job nobody is running still read "právě vznikají", for as long
//     as it sat there - the F113 class of lie, a promise with no work behind
//     it. MEASURED on dev D1 2026-08-13: a job created 09:14 was not claimed
//     until it was claimed, and on PROD the two real jobs waited 29 and 12
//     minutes before the runner even started them. A runner that dies leaves
//     that sentence on the client's screen forever.
//   * a run that ENDED and found nothing said "bereme si to na starost a
//     parťák se ti ozve" - it sent the client away to wait, when the one thing
//     that can unblock them is standing on the same screen: the microphone.
//
// ONE derivation, on the server, consumed by the surface. Nothing may
// re-derive "is research running" from its own DTO (the fixr5 law).

/** What the profile step may honestly say about the initial research. */
export type ProfileResearchState =
  /** A job is queued or running and is still inside its normal time. */
  | "running"
  /** A job exists but nothing is picking it up any more. Ours to fix. */
  | "delayed"
  /** Research ran and found nothing usable. The client's voice is the way on. */
  | "empty"
  /** No research was ever queued for this client. */
  | "none";

/**
 * How long a queued job may wait before the copy stops saying "právě
 * vznikají" and admits the wait is too long (the F110 shape).
 *
 * CALIBRATED, not guessed. Measured 2026-08-13 over every finished job:
 *   dev  (25 done): 214 s, 240 s, 857 s, 913 s, 1 068 s, 1 846 s, 1 856 s in
 *        the healthy cluster - i.e. 3.5 to 31 minutes end to end.
 *   prod (2 done):  858 s and 1 920 s (14 and 32 minutes), of which 12 and 29
 *        minutes were spent waiting to be CLAIMED.
 * Everything above that band (3 h, 9.8 h, 11 h, 2 days) is a run the machine
 * was not there for. 90 minutes is ~3x the slowest healthy run, so a client
 * whose research is genuinely working never meets this state, and one whose
 * runner is gone hears the truth inside the same working hour.
 */
export const PROFILE_RESEARCH_DELAY_MS = 90 * 60_000;

/**
 * The pending endpoint offers a job only `WHERE attempts < RESEARCH_MAX_ATTEMPTS`
 * (worker/src/stripe-import.ts). A job that burned its attempts and is still
 * not terminal can therefore never be picked up again - it is dead the moment
 * it happens, not 90 minutes later. Kept in step with the Worker's own
 * constant by `tests/profilnenalezen-f145.test.ts`.
 */
export const PROFILE_RESEARCH_MAX_ATTEMPTS = 3;

export interface ProfileResearchFacts {
  /** Status of the LATEST job, or null when the client has none. */
  status: "pending" | "running" | "done" | "failed" | "canceled" | null;
  /** How many times a runner has claimed it. */
  attempts: number;
  /** When the job entered the queue. */
  createdAt: string | null;
  /** When a runner last claimed it; the clock a running job is measured on. */
  claimedAt: string | null;
  /** Does a readable dossier exist for this client? */
  dossierReady: boolean;
}

/**
 * 🔴 A `done` job is NOT this module's business. It produced a dossier, so the
 * profile view renders the map (or, with every fact excluded, its own
 * found-nothing state, which shares this state's copy). Answering "none" for
 * it is deliberate: there is no research to narrate any more.
 */
export function profileResearchStateFrom(
  facts: ProfileResearchFacts,
  now = Date.now()
): ProfileResearchState {
  // A readable dossier outranks every job status. A second research run over a
  // client who already HAS a profile must never blank their own screen back to
  // "we found nothing" while it works.
  if (facts.dossierReady) return "none";
  if (facts.status === "failed") return "empty";
  if (facts.status !== "pending" && facts.status !== "running") return "none";
  if (facts.attempts >= PROFILE_RESEARCH_MAX_ATTEMPTS) return "delayed";
  // A claimed job is measured from the claim: the runner has it and is working.
  // An unclaimed one is measured from the moment it joined the queue.
  const since = facts.status === "running" && facts.claimedAt
    ? Date.parse(facts.claimedAt)
    : facts.createdAt ? Date.parse(facts.createdAt) : Number.NaN;
  // An unreadable timestamp is never turned into an age. Saying "this is
  // taking too long" needs a measurement; without one the honest answer is the
  // one we can defend, and the job really is queued.
  if (!Number.isFinite(since)) return "running";
  return now - since >= PROFILE_RESEARCH_DELAY_MS ? "delayed" : "running";
}

/**
 * Does the step get to draw the working marker and promise a build?
 * The ONE reading of "is the machine on this right now", so the avatar, the
 * title and the sentence cannot disagree on one screen.
 */
export function profileResearchIsWorking(state: ProfileResearchState): boolean {
  return state === "running";
}
